import React, { useState, useRef, useEffect } from "react";
import "./News.css";
import FairsData from "../../api/FairsData";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const IconPin = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const IconCal = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const SWIPER_BP = {
  0: { slidesPerView: 1, spaceBetween: 16 },
  640: { slidesPerView: 1.1, spaceBetween: 20 },
  900: { slidesPerView: 2, spaceBetween: 24 },
  1280: { slidesPerView: 2, spaceBetween: 28 },
};

const FairSlider = ({ items, onOpen }) => (
  <Swiper
    modules={[Navigation, Pagination, A11y]}
    breakpoints={SWIPER_BP}
    navigation
    pagination={{ clickable: true }}
    className="fair-swiper"
  >
    {items.map((fuar) => (
      <SwiperSlide key={fuar.id}>
        <div className="fuar-card" onClick={() => onOpen(fuar.image)}>
          <div className="fuar-card-img-wrap">
            <img src={fuar.image} alt={fuar.title || "fuar"} loading="lazy" />
            <div className="fuar-card-overlay">
              <span className="fuar-card-zoom">+</span>
            </div>
          </div>
          {(fuar.title || fuar.location || fuar.date || fuar.description) && (
            <div className="fuar-card-body">
              {fuar.title && <h5 className="fuar-card-title">{fuar.title}</h5>}
              {fuar.location && (
                <p className="fuar-card-meta">
                  <IconPin /> {fuar.location}
                </p>
              )}
              {fuar.date && (
                <p className="fuar-card-meta">
                  <IconCal /> {fuar.date}
                </p>
              )}
              {fuar.description && (
                <p className="fuar-card-desc">{fuar.description}</p>
              )}
            </div>
          )}
        </div>
      </SwiperSlide>
    ))}
  </Swiper>
);

const News = () => {
  const { t } = useTranslation();
  const setSeo = useSEO();

  useEffect(() => {
    setSeo({
      title: t("news.meta.title"),
      description: t("news.meta.description"),
      keywords: t("news.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);

  const [selectedImage, setSelectedImage] = useState(null);
  const modalRef = useRef(null);

  const openModal = (img) => {
    setSelectedImage(img);
    const modal = new window.bootstrap.Modal(modalRef.current);
    modal.show();
  };

  return (
    <>
      {/* ── Hero ── */}
      <section className="news-hero">
        <div className="news-hero-inner">
          <p className="news-hero-eyebrow">{t("news.eyebrow")}</p>
          <h1 className="news-hero-title">MÜKEMEL</h1>
          <div className="news-hero-divider" />
          <p className="news-hero-desc">{t("news.heroDesc")}</p>
        </div>
      </section>

      <div className="container news-page-container">
        {/* ── Bölüm 1: Katıldığımız Fuarlar ── */}
        <section className="news-section">
          <div className="news-section-head">
            <span className="news-section-line" />
            <h2 className="news-section-title">{t("news.section.attended")}</h2>
            <span className="news-section-line" />
          </div>
          <FairSlider items={FairsData.attended} onOpen={openModal} />
        </section>

        {/* ── Bölüm 2: Fuar Planları ── */}
        <section className="news-section">
          <div className="news-section-head">
            <span className="news-section-line" />
            <h2 className="news-section-title">{t("news.section.upcoming")}</h2>
            <span className="news-section-line" />
          </div>
          <FairSlider items={FairsData.upcoming} onOpen={openModal} />
        </section>
      </div>

      {/* ── Bölüm 3: Finalist & Ödüller ── */}
      <section className="awards-section">
        <div className="container">
          <div className="news-section-head awards-head">
            <span className="news-section-line awards-line" />
            <h2 className="news-section-title awards-title">
              {t("news.section.finalist")}
            </h2>
            <span className="news-section-line awards-line" />
          </div>

          <div className="awards-groups-row">
            {FairsData.awards.map((group) => (
              <div
                key={`${group.groupKey}-${group.labelKey}`}
                className="awards-group-col"
              >
                <p className="awards-group-label">{t(group.labelKey)}</p>
                <div className="awards-logos">
                  {group.items.map((item) => (
                    <div key={item.id} className="awards-logo-item">
                      <img src={item.image} alt={item.title} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Modal ── */}
      <div
        className="modal fade"
        tabIndex="-1"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content border-0">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body text-center px-3 pb-4">
              {selectedImage && (
                <img
                  src={selectedImage}
                  alt={t("blog.imagePreview")}
                  className="img-fluid"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default News;
